import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, Send, Sparkles, ThumbsUp, Clock, Shield, Zap, Heart, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';
import { toast } from 'sonner';

const BADGE_OPTIONS = [
    { id: 'reactif', label: 'Réactif', icon: Zap, color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
    { id: 'professionnel', label: 'Professionnel', icon: Shield, color: 'text-blue-400 bg-blue-400/10 border-blue-400/30' },
    { id: 'a_lecoute', label: 'À l\'écoute', icon: Heart, color: 'text-pink-400 bg-pink-400/10 border-pink-400/30' },
    { id: 'ponctuel', label: 'Ponctuel', icon: Clock, color: 'text-green-400 bg-green-400/10 border-green-400/30' },
    { id: 'efficace', label: 'Efficace', icon: ThumbsUp, color: 'text-purple-400 bg-purple-500/10 border-purple-500/30' },
    { id: 'transparent', label: 'Transparent', icon: MessageCircle, color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30' },
];

const STEP_LABELS = {
    'signature_mandat': 'Signature du mandat',
    'mensuel': 'Suivi mensuel',
    'offre': 'Réception d\'une offre',
    'compromis': 'Signature du compromis',
    'acte_authentique': 'Acte authentique (vente finale)',
};

function StarRating({ value, onChange, size = 'lg' }) {
    const [hover, setHover] = useState(0);
    const sizeClass = size === 'lg' ? 'w-10 h-10' : 'w-7 h-7';
    
    return (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(star => (
                <button
                    key={star}
                    type="button"
                    onClick={() => onChange(star)}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(0)}
                    className="transition-transform hover:scale-110"
                >
                    <Star className={`${sizeClass} transition-colors ${
                        star <= (hover || value) 
                            ? 'text-yellow-400 fill-yellow-400' 
                            : 'text-white/20'
                    }`} />
                </button>
            ))}
        </div>
    );
}

export default function SellerRatingModal({ isOpen, onClose, mandate, agentName, ratingStep, onComplete }) {
    const [step, setStep] = useState(1); // 1=badges, 2=notes, 3=commentaire (only for acte_authentique)
    const [badges, setBadges] = useState([]);
    const [criteria, setCriteria] = useState({ suivi: 0, disponibilite: 0, professionnalisme: 0 });
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    
    const isFinal = ratingStep === 'acte_authentique';
    const totalSteps = isFinal ? 3 : 2;
    const stepLabel = STEP_LABELS[ratingStep] || 'Évaluation';

    const toggleBadge = (id) => {
        setBadges(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
    };

    const overall = Object.values(criteria).reduce((a, b) => a + b, 0) / Math.max(Object.values(criteria).filter(v => v > 0).length, 1);

    const canSubmit = () => {
        if (step === 1) return badges.length > 0;
        if (step === 2) return criteria.suivi > 0 && criteria.disponibilite > 0 && criteria.professionnalisme > 0;
        if (step === 3) return true; // comment optional
        return false;
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await api.post('/ratings/seller-evaluate', {
                mandate_id: mandate?.id,
                agent_id: mandate?.agent_id,
                rating_step: ratingStep,
                badges: badges,
                criteria: criteria,
                overall: Math.round(overall * 10) / 10,
                comment: isFinal ? comment : null
            });
            toast.success('Merci pour votre évaluation !');
            onComplete?.();
            onClose();
            // Reset
            setStep(1);
            setBadges([]);
            setCriteria({ suivi: 0, disponibilite: 0, professionnalisme: 0 });
            setComment('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-[#1a1a2e] rounded-2xl w-full max-w-lg border border-white/10 shadow-2xl overflow-hidden"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="p-5 border-b border-white/10">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-[#FF914D]" />
                                <span className="text-xs text-[#FF914D] font-bold uppercase tracking-wider">{stepLabel}</span>
                            </div>
                            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg transition-colors">
                                <X className="w-5 h-5 text-white/40" />
                            </button>
                        </div>
                        <h2 className="text-lg font-bold text-white">
                            Comment se passe le suivi avec {agentName || 'votre agent'} ?
                        </h2>
                        {/* Progress */}
                        <div className="flex gap-1 mt-3">
                            {Array.from({ length: totalSteps }).map((_, i) => (
                                <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < step ? 'bg-gradient-to-r from-[#681793] to-[#FF914D]' : 'bg-white/10'}`} />
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                        <AnimatePresence mode="wait">
                            {/* STEP 1: Badges */}
                            {step === 1 && (
                                <motion.div key="badges" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                    <p className="text-sm text-white/60 mb-4">Quels mots décrivent le mieux votre agent ?</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {BADGE_OPTIONS.map(badge => {
                                            const selected = badges.includes(badge.id);
                                            return (
                                                <button
                                                    key={badge.id}
                                                    onClick={() => toggleBadge(badge.id)}
                                                    className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all text-left ${
                                                        selected 
                                                            ? `${badge.color} border-current scale-[1.02]`
                                                            : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
                                                    }`}
                                                >
                                                    <badge.icon className={`w-5 h-5 flex-shrink-0 ${selected ? '' : 'text-white/30'}`} />
                                                    <span className="text-sm font-medium">{badge.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            )}

                            {/* STEP 2: Star ratings */}
                            {step === 2 && (
                                <motion.div key="stars" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                    <p className="text-sm text-white/60 mb-5">Notez votre agent sur ces 3 critères :</p>
                                    <div className="space-y-5">
                                        <div>
                                            <p className="text-sm font-medium text-white mb-2">Qualité du suivi</p>
                                            <p className="text-xs text-white/40 mb-2">Vous tient-il informé régulièrement de l'avancement ?</p>
                                            <StarRating value={criteria.suivi} onChange={v => setCriteria({...criteria, suivi: v})} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-white mb-2">Disponibilité</p>
                                            <p className="text-xs text-white/40 mb-2">Est-il réactif quand vous le contactez ?</p>
                                            <StarRating value={criteria.disponibilite} onChange={v => setCriteria({...criteria, disponibilite: v})} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-white mb-2">Professionnalisme</p>
                                            <p className="text-xs text-white/40 mb-2">Est-il clair, compétent et de confiance ?</p>
                                            <StarRating value={criteria.professionnalisme} onChange={v => setCriteria({...criteria, professionnalisme: v})} />
                                        </div>
                                    </div>
                                    {overall > 0 && (
                                        <div className="mt-4 p-3 bg-white/5 rounded-xl flex items-center justify-between">
                                            <span className="text-sm text-white/60">Note globale</span>
                                            <div className="flex items-center gap-2">
                                                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                                <span className="text-lg font-bold text-white">{overall.toFixed(1)}</span>
                                                <span className="text-xs text-white/40">/ 5</span>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            )}

                            {/* STEP 3: Comment (final only) */}
                            {step === 3 && isFinal && (
                                <motion.div key="comment" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                    <p className="text-sm text-white/60 mb-2">Votre avis sera visible sur le profil public de l'agent.</p>
                                    <p className="text-xs text-white/40 mb-4">Partagez votre expérience pour aider les futurs vendeurs.</p>
                                    <textarea
                                        value={comment}
                                        onChange={e => setComment(e.target.value)}
                                        placeholder="L'agent a été très professionnel tout au long de la vente..."
                                        rows={5}
                                        maxLength={800}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 text-sm resize-none focus:outline-none focus:border-[#FF914D]/50"
                                    />
                                    <p className="text-xs text-white/30 mt-1 text-right">{comment.length}/800</p>
                                    
                                    {/* Summary */}
                                    <div className="mt-4 p-4 bg-gradient-to-r from-[#681793]/10 to-[#FF914D]/10 rounded-xl border border-[#FF914D]/20">
                                        <p className="text-xs text-white/50 mb-2">Récapitulatif de votre évaluation</p>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                            <span className="font-bold text-white">{overall.toFixed(1)}/5</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                            {badges.map(b => {
                                                const badge = BADGE_OPTIONS.find(bo => bo.id === b);
                                                return badge ? (
                                                    <span key={b} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60">{badge.label}</span>
                                                ) : null;
                                            })}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Footer */}
                    <div className="p-5 border-t border-white/10 flex items-center justify-between">
                        <div>
                            {step > 1 && (
                                <button onClick={() => setStep(s => s - 1)} className="text-sm text-white/50 hover:text-white">
                                    Précédent
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={onClose} className="text-xs text-white/30 hover:text-white/50 mr-2">
                                Plus tard
                            </button>
                            {step < totalSteps ? (
                                <Button
                                    onClick={() => setStep(s => s + 1)}
                                    disabled={!canSubmit()}
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white"
                                >
                                    Suivant
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canSubmit() || submitting}
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white"
                                >
                                    {submitting ? 'Envoi...' : (
                                        <>
                                            <Send className="w-4 h-4 mr-2" />
                                            Envoyer
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
